import { collection, getDocs, query, where, limit as fLimit } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import type { AiReference } from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

export type AiReferencesState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; items: AiReference[] }
  | { status: 'error'; error: Error };

/**
 * Sprint BG-DataSettings 24: 参考事例 DB (旧 references.json 移植) を emotion で絞込んでロード。
 * emotion null/undefined のときは「全体」のみフィルタ。Firestore 'in' は 30 値まで OK。
 */
export function useAiReferences(emotions: string[] | null): AiReferencesState {
  const [state, setState] = useState<AiReferencesState>({ status: 'idle' });
  // emotion list を join した安定文字列を deps とする (配列リテラル直接 deps は ESLint で警告 + 不安定)
  const sig = emotions === null ? null : emotions.join(',');
  useEffect(() => {
    let cancelled = false;
    if (emotions === null) {
      setState({ status: 'idle' });
      return;
    }
    setState({ status: 'loading' });
    const filterValues = Array.from(new Set([...(emotions ?? []), '全体']))
      .filter((v): v is string => Boolean(v))
      .slice(0, 30);
    (async () => {
      try {
        const q =
          filterValues.length > 0
            ? query(
                collection(db, 'aiReferences'),
                where('emotion', 'in', filterValues),
                fLimit(100),
              )
            : query(collection(db, 'aiReferences'), fLimit(100));
        const snap = await getDocs(q);
        if (cancelled) return;
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as AiReference[];
        setState({ status: 'ready', items });
      } catch (e) {
        if (!cancelled) setState({ status: 'error', error: e as Error });
      }
    })();
    return () => {
      cancelled = true;
    };
    // sig captures the emotions array content; emotions itself is intentionally not in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);
  return state;
}
